import { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Upload, Image, Video, FileText, Mic, Check, Trash2, Link2, Copy, Loader2, Images, Clock, Wand2, Palette } from "lucide-react";
import api, { fileUrl } from "@/lib/api";
import { chunkedUpload } from "@/lib/upload";
import { TopBar } from "@/pages/Dashboard";

const KIND_ICON = { photo: Image, video: Video, document: FileText, voice: Mic };

function kindFor(file) {
  const t = file.type || "";
  if (t.startsWith("image/")) return "photo";
  if (t.startsWith("video/")) return "video";
  if (t.startsWith("audio/")) return "voice";
  return "document";
}

async function uploadAny(file) {
  const kind = kindFor(file);
  if (kind === "photo" || kind === "document") {
    const fd = new FormData(); fd.append("file", file);
    const r = await api.post("/uploads/simple", fd, { headers: { "Content-Type": "multipart/form-data" } });
    return { file_id: r.data.file_id, kind };
  }
  const up = await chunkedUpload(file, file.name, file.type);
  return { file_id: up.file_id, kind };
}

function MediaCard({ m, premium, onApprove, onDelete, onRestore, restoring }) {
  const Icon = KIND_ICON[m.kind] || FileText;
  const displayId = m.restored_file_id || m.file_id;
  return (
    <div className="rounded-xl border border-[hsl(var(--film-border))] bg-[hsl(var(--card))] overflow-hidden" data-testid={`media-card-${m.media_id}`}>
      <div className="aspect-square bg-[hsl(var(--muted))] grid place-items-center relative">
        {m.kind === "photo" ? <img src={fileUrl(displayId)} alt={m.who_what || "photo"} className="h-full w-full object-cover" />
          : <Icon size={30} className="text-[hsl(var(--muted-foreground))]" />}
        {!m.approved && <span className="absolute top-2 left-2 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-[hsl(var(--chapter-warn))] text-white">Pending</span>}
        {m.restored_file_id && <span className="absolute top-2 right-2 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))]" data-testid={`media-restored-${m.media_id}`}>{m.restored_mode === "colorize" ? "Colorized" : "Restored"}</span>}
        {restoring === m.media_id && <div className="absolute inset-0 grid place-items-center bg-black/50 text-white"><div className="text-center"><Loader2 className="mx-auto animate-spin" /><p className="mt-1 text-xs">Restoring…</p></div></div>}
      </div>
      <div className="p-2.5">
        <div className="text-xs font-semibold truncate">{m.who_what || m.kind}</div>
        {(m.when_text || m.contributor_name) && <div className="text-[11px] text-[hsl(var(--muted-foreground))] truncate">{[m.when_text, m.source === "family" ? `by ${m.contributor_name}` : null].filter(Boolean).join(" · ")}</div>}
        {(m.memory_text || m.memory_transcript) && <p className="mt-1 text-[11px] text-[hsl(var(--muted-foreground))] line-clamp-2 italic">"{m.memory_text || m.memory_transcript}"</p>}
        {m.kind === "photo" && premium && m.approved && (
          <div className="mt-2 flex gap-1.5">
            <button data-testid={`media-enhance-${m.media_id}`} onClick={() => onRestore(m, "enhance")} disabled={!!restoring} className="flex-1 inline-flex items-center justify-center gap-1 h-7 rounded-lg text-[11px] font-semibold border border-[hsl(var(--border))] hover:bg-[hsl(var(--muted))] disabled:opacity-50"><Wand2 size={12} /> Enhance</button>
            <button data-testid={`media-colorize-${m.media_id}`} onClick={() => onRestore(m, "colorize")} disabled={!!restoring} className="flex-1 inline-flex items-center justify-center gap-1 h-7 rounded-lg text-[11px] font-semibold border border-[hsl(var(--border))] hover:bg-[hsl(var(--muted))] disabled:opacity-50"><Palette size={12} /> Colorize</button>
          </div>
        )}
        <div className="mt-2 flex gap-1.5">
          {!m.approved && <button data-testid={`media-approve-${m.media_id}`} onClick={() => onApprove(m)} className="flex-1 inline-flex items-center justify-center gap-1 h-8 rounded-lg text-xs font-semibold bg-[hsl(var(--accent))]/12 text-[hsl(var(--accent))] hover:bg-[hsl(var(--accent))]/20"><Check size={13} /> Approve</button>}
          <button data-testid={`media-delete-${m.media_id}`} onClick={() => onDelete(m)} className="h-8 w-8 grid place-items-center rounded-lg text-[hsl(var(--destructive))] hover:bg-[hsl(var(--destructive))]/10"><Trash2 size={14} /></button>
        </div>
      </div>
    </div>
  );
}

export default function StoryArchive() {
  const { storyId } = useParams();
  const navigate = useNavigate();
  const [story, setStory] = useState(null);
  const [tab, setTab] = useState("vault");
  const [media, setMedia] = useState([]);
  const [pending, setPending] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [restoring, setRestoring] = useState("");
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const [st, md] = await Promise.all([
        api.get(`/stories/${storyId}`),
        api.get(`/stories/${storyId}/media`, { params: { status: tab === "pending" ? "pending" : "approved" } }),
      ]);
      setStory(st.data); setMedia(md.data.media || []); setPending(md.data.pending_count || 0);
    } catch (e) { if (e?.response?.status === 404) navigate("/dashboard"); }
  }, [storyId, tab, navigate]);
  useEffect(() => { load(); }, [load]);

  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const f of files) {
        const { file_id, kind } = await uploadAny(f);
        await api.post(`/stories/${storyId}/media`, { kind, file_id, who_what: f.name.replace(/\.[^.]+$/, "") });
      }
      toast.success(`${files.length} item${files.length > 1 ? "s" : ""} added to the vault`);
      setTab("vault"); await load();
    } catch { toast.error("Some files could not be uploaded"); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const approve = async (m) => { await api.post(`/stories/${storyId}/media/${m.media_id}/approve`); toast.success("Added to the vault"); load(); };
  const del = async (m) => { if (!window.confirm("Remove this item?")) return; await api.delete(`/stories/${storyId}/media/${m.media_id}`); load(); };
  const restore = async (m, mode) => {
    setRestoring(m.media_id);
    try {
      const r = await api.post(`/stories/${storyId}/media/${m.media_id}/restore`, { mode });
      setMedia((ms) => ms.map((x) => x.media_id === m.media_id ? { ...x, restored_file_id: r.data.restored_file_id, restored_mode: r.data.restored_mode } : x));
      toast.success(mode === "colorize" ? "Photo colorized" : "Photo restored");
    } catch (e) { toast.error(e?.response?.data?.detail || "Restoration failed"); }
    finally { setRestoring(""); }
  };

  const shareUrl = story ? `${window.location.origin}/s/${story.slug}` : "";
  const interviewUrl = story ? `${window.location.origin}/si/${story.slug}` : "";
  const copy = () => { navigator.clipboard.writeText(shareUrl); toast.success("Collection link copied"); };
  const copyInterview = () => { navigator.clipboard.writeText(interviewUrl); toast.success("Interview link copied"); };

  if (!story) return <div className="min-h-screen"><TopBar /></div>;
  const ac = story.archive_counts || {};

  return (
    <div className="min-h-screen" data-testid="story-archive">
      <TopBar />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        <button onClick={() => navigate(`/stories/${storyId}`)} className="inline-flex items-center gap-1.5 text-sm text-[hsl(var(--muted-foreground))] hover:underline"><ArrowLeft size={15} /> Back to story</button>
        <div className="mt-3 flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="font-display text-3xl font-semibold flex items-center gap-2"><Images size={22} className="text-[hsl(var(--accent))]" /> Archive vault</h1>
            <p className="text-[hsl(var(--muted-foreground))]">Photos, videos, letters and voice memories for {story.honoree_name}'s story.</p>
          </div>
          <div className="font-mono text-xs text-[hsl(var(--muted-foreground))] text-right">
            <div className="text-2xl font-display text-[hsl(var(--foreground))]">{(ac.photo||0)+(ac.video||0)+(ac.document||0)+(ac.voice||0)}</div>
            items · {ac.memories || 0} memories
          </div>
        </div>

        {/* family collection link */}
        <div className="mt-5 rounded-2xl border border-[hsl(var(--film-border))] bg-[hsl(var(--secondary))] p-4 flex flex-wrap items-center gap-3" data-testid="story-collection-link">
          <Link2 size={18} className="text-[hsl(var(--accent))]" />
          <div className="flex-1 min-w-[220px]">
            <div className="text-sm font-semibold">Family Memory Collection link</div>
            <div className="font-mono text-xs text-[hsl(var(--muted-foreground))] truncate">{shareUrl}</div>
          </div>
          <button data-testid="copy-collection-link" onClick={copy} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] text-sm font-semibold"><Copy size={15} /> Copy link</button>
        </div>

        {/* remote interview link */}
        <div className="mt-3 rounded-2xl border border-[hsl(var(--film-border))] bg-[hsl(var(--secondary))] p-4 flex flex-wrap items-center gap-3" data-testid="story-interview-link">
          <Mic size={18} className="text-[hsl(var(--accent))]" />
          <div className="flex-1 min-w-[220px]">
            <div className="text-sm font-semibold">Remote interview link</div>
            <div className="font-mono text-xs text-[hsl(var(--muted-foreground))] truncate">{interviewUrl}</div>
            <div className="text-[11px] text-[hsl(var(--muted-foreground))]">Relatives record full AI-guided chapters — no account needed.</div>
          </div>
          <button data-testid="copy-interview-link" onClick={copyInterview} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl border border-[hsl(var(--border))] text-sm font-semibold hover:bg-[hsl(var(--muted))]"><Copy size={15} /> Copy link</button>
        </div>

        {/* upload + tabs */}
        <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
          <div className="inline-flex rounded-xl border border-[hsl(var(--border))] overflow-hidden">
            <button data-testid="archive-tab-vault" onClick={() => setTab("vault")} className={`px-4 py-2 text-sm font-semibold ${tab === "vault" ? "bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))]" : "hover:bg-[hsl(var(--muted))]"}`}>Vault</button>
            <button data-testid="archive-tab-pending" onClick={() => setTab("pending")} className={`px-4 py-2 text-sm font-semibold inline-flex items-center gap-1.5 ${tab === "pending" ? "bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))]" : "hover:bg-[hsl(var(--muted))]"}`}><Clock size={14} /> Pending {pending > 0 && <span className="px-1.5 rounded-full bg-[hsl(var(--chapter-warn))] text-white text-[10px]">{pending}</span>}</button>
          </div>
          <input ref={fileRef} type="file" multiple accept="image/*,video/*,audio/*,.pdf" onChange={onFiles} className="hidden" data-testid="archive-file-input" />
          <button data-testid="archive-upload-button" onClick={() => fileRef.current?.click()} disabled={uploading}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] font-semibold hover:opacity-90 disabled:opacity-60">
            {uploading ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />} Add files
          </button>
        </div>

        {media.length === 0 ? (
          <div className="mt-6 rounded-2xl border border-dashed border-[hsl(var(--border))] p-12 text-center text-[hsl(var(--muted-foreground))]">
            {tab === "pending" ? "No pending family submissions." : "The vault is empty. Add photos and videos, or share the collection link with the family."}
          </div>
        ) : (
          <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3" data-testid="archive-grid">
            {media.map((m) => <MediaCard key={m.media_id} m={m} premium={story.plan_tier === "story_premium"} restoring={restoring} onApprove={approve} onDelete={del} onRestore={restore} />)}
          </div>
        )}
      </main>
    </div>
  );
}
